import { useEffect, useState, useRef } from "react";
import { Button, Badge } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import dayjs from "dayjs";

import DropdownOptionWithIcon from "../../Menus/DropdownOptionWithIcon";
import EditableText from "../../../elements/EditableText";
import DocumentSettingsDrawer from "../../Drawers/DocumentSettingsDrawer";
import PopoverDropdownMenu from "../../Menus/PopoverDropdownMenu";
import IconButton from "../../../elements/IconButton";
import DropdownMenuDivider from "../../Menus/DropdownMenuDivider";
import ShareScreenDrawer from "../ShareScreenDrawer/ShareScreenDrawer";

interface DocumentToolbarProps {
  writeMode: boolean;
  onCancel: () => void;
  onSave: () => void;
  onEdit: () => void;
  onTitleSave: (title: string) => void;
  onSettingsSave: (type: string, audience: string, onSave: any) => void;
  document: any;
  loading: boolean;
  handleBack?: () => void;
  className?: string;
}

const DocumentToolbar = ({
  writeMode,
  onCancel,
  onSave,
  onEdit,
  onTitleSave,
  onSettingsSave,
  document,
  loading,
  handleBack,
  className,
}: DocumentToolbarProps) => {
  const [title, setTitle] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSettings, setIsEditingSettings] = useState(false);
  const [shareDocumentDrawer, setShareDocumentDrawer] = useState(false);

  const titleRef = useRef<any>();
  const documentOptionsRef = useRef<any>();
  const isShared = window.location.pathname.startsWith("/shared");

  const handleTitleChange = (newTitle: string) => setTitle(newTitle);

  const handleTitleSave = () => {
    onTitleSave(title);
  };

  const toggleEditSettings = () => {
    setIsEditingSettings(true);
  };

  useEffect(() => {
    if (loading) return;

    setTitle(document?.Name);
  }, [loading, document?.Name]);

  const DocumentOptions = () => (
    <PopoverDropdownMenu>
      <DropdownOptionWithIcon
        name="Share Document"
        icon="mdi mdi-share-variant"
        onClick={() => {
          setShareDocumentDrawer(true);
          documentOptionsRef.current.close();
        }}
      />
      <DropdownMenuDivider />
      <DropdownOptionWithIcon
        name="Edit Title"
        onClick={() => {
          setIsEditingTitle(true);
          titleRef.current.focus();
          titleRef.current.scrollIntoView();
          documentOptionsRef.current.close();
        }}
        icon="mdi mdi-pencil"
      />
      <DropdownOptionWithIcon
        name="Edit Document Type"
        icon="mdi mdi-pencil"
        onClick={() => {
          toggleEditSettings();
          documentOptionsRef.current.close();
        }}
      />
    </PopoverDropdownMenu>
  );

  if (loading) {
    return <div className="skeleton" style={{ width: 150, height: 20 }}></div>;
  }

  return (
    <>
      <div
        className={`tw:flex tw:items-center tw:justify-between tw:w-11/12 tw:md:w-full  ${className}`}
      >
        <div className="tw:flex tw:md:w-8/12 tw:w-full">
          {/* Icon */}

          {handleBack && (
            <IconButton
              icon="chevron-left"
              onClick={handleBack}
              className="mr-2"
            />
          )}

          {/* Title */}
          <div className="tw:max-w-9/10">
            <h2 className="d-flex align-items-center m-0 tw:w-full font-weight-500">
              <EditableText
                ref={titleRef}
                savedText={document?.Name || "Document"}
                editStyles={{ width: "calc(100vh-30%)" }}
                text={title}
                onChange={handleTitleChange}
                onSave={handleTitleSave}
                disabled={loading || isShared}
                externalTrigger={isEditingTitle}
                setExternalTrigger={setIsEditingTitle}
              />
              {document?.Shared && (
                <i className="mdi mdi-share-variant text-primary ml-2" />
              )}
            </h2>
            {!isShared && (
              <div className="text-xs font-weight-500">
                Updated By{" "}
                <span className="text-primary">
                  {document?.LastUpdatedBy?._RecordName ?? "Unknown"}
                </span>
                {` on ${dayjs(document?.LastUpdated).format("MMM D, YYYY")}`}
              </div>
            )}
          </div>
        </div>
        {/* Buttons */}
        {!isShared && (
          <div
            id="document-buttons-container"
            className="tw:flex tw:md:flex-row tw:flex-col tw:items-center"
          >
            {document?.Type?.length > 0 && (
              <div className="tw:hidden tw:md:inline-block">
                <span className="tw:mr-3">
                  {document.Type?.map((t: string, i: number) => (
                    <Badge color="light" key={i}>
                      {t}
                    </Badge>
                  ))}
                </span>
              </div>
            )}

            {writeMode ? (
              <>
                <div className="tw:flex tw:flex-col tw:md:w-36 tw:w-auto tw:md:flex-row tw:justify-between tw:items-center tw:h-auto ">
                  <Button
                    size="sm"
                    className="tw:md:bg-white tw:bg-danger tw:m-0 tw:mb-4 tw:md:mb-0  tw:w-11 tw:h-11 tw:md:h-auto tw:md:w-auto tw:static tw:border-transparent tw:border-2"
                    onClick={onCancel}
                  >
                    <i className="mdi mdi-close tw:m-0 tw:text-2xl tw:text-white tw:inline tw:md:hidden tw:absolute tw:left-2 tw:top-1" />
                    <div className="tw:hidden tw:md:inline tw:font-bold p-0 m-0">
                      Cancel
                    </div>
                  </Button>
                  <Button
                    size="sm"
                    className="tw:m-0 tw:mb-4 tw:md:mb-0 tw:w-11 tw:h-11 tw:md:h-auto tw:md:w-auto tw:static"
                    color="primary"
                    onClick={onSave}
                  >
                    <i className="mdi mdi-content-save-outline tw:m-0 tw:text-2xl tw:text-white tw:inline tw:md:hidden tw:absolute tw:left-2.5 tw:top-1.5 tw:md:left-1.5"></i>
                    <div className="tw:hidden tw:md:inline tw:font-bold tw:-0 tw:m-0 tw:px-2.5">
                      Save
                    </div>
                  </Button>
                </div>
              </>
            ) : (
              <Button
                size="sm"
                color="secondary"
                className="m-0 tw:m-0 tw:mb-4 tw:md:mb-0 tw:w-11 tw:md:w-8 tw:md:h-8 tw:h-11 tw:static tw:md:border-0 tw:md:hover:outline-1 tw:md:outline-gray-200 tw:md:bg-white"
                onClick={onEdit}
              >
                <i className="mdi mdi-pencil tw:m-0 tw:text-2xl tw:md:text-lg  tw:absolute tw:md:left-1.5 tw:md:top-0.5 tw:left-2.5 tw:top-1.5"></i>
              </Button>
            )}

            <Whisper
              placement="autoVerticalEnd"
              trigger="click"
              ref={documentOptionsRef}
              speaker={
                <Popover className="sidebar-popover" full>
                  <DocumentOptions />
                </Popover>
              }
            >
              <Button
                size="sm"
                color="secondary"
                className="m-0 tw:md:ml-2 tw:ml-0 tw:m-0 tw:mb-2 tw:md:mb-0 tw:w-11 tw:md:w-8 tw:md:h-8 tw:h-11 tw:static tw:md:border-0 tw:md:bg-white tw:md:hover:outline-1 tw:md:outline-gray-200"
              >
                <i className="mdi mdi-dots-horizontal tw:m-0 tw:text-2xl tw:md:text-xl tw:absolute tw:md:left-1.5 tw:md:top-0.5 tw:left-2.5 tw:top-1.5"></i>
              </Button>
            </Whisper>
          </div>
        )}
      </div>
      <hr className="my-3"></hr>
      <DocumentSettingsDrawer
        open={isEditingSettings}
        onClose={() => setIsEditingSettings(false)}
        document={document}
        onSave={onSettingsSave}
      />
      <ShareScreenDrawer
        open={shareDocumentDrawer}
        onClose={() => setShareDocumentDrawer(false)}
        documentID={document?._id}
        viewID={undefined}
        recordID={undefined}
        pageID={undefined}
        parentViewID={undefined}
        recordModule={undefined}
      />
    </>
  );
};

export default DocumentToolbar;
